import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';

import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import DummyCellComponent from 'webapp/components/dummy-cell';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import OptionsButtonComponent from 'webapp/components/options-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type ActivityLog from 'webapp/models-custom/activity-log';
import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import Report from 'webapp/models-custom/report';

interface Args {
	model: ActivityLog;
	showAction?: boolean;
	showAccount?: boolean;
	openEmailDetailsDialog?: (uniqueReferenceId: string | null) => void;
}

type Action = 'viewObject' | 'doNothing';

export default class AccountLogsTableRowComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;

	get isActivityLogDisabled() {
		return !this.billingService.checkIfCan(BillingFeature.ACTIVITY_LOG);
	}

	get options(): PopupListItem<Action>[] {
		const options: PopupListItem<Action>[] = [],
			modelObject = this.args.model.object;

		if (this.isActivityLogDisabled) {
			options.push({
				icon: 'Pencil',
				text: 'View',
				action: 'doNothing',
			});
		} else if (
			modelObject instanceof Board ||
			modelObject instanceof BoardLoop ||
			modelObject instanceof Report
		) {
			options.push({
				icon: 'Pencil',
				text: `View ${this.args.model.modelTypeName}`,
				action: 'viewObject',
			});
		} else if (this.args.model.isEmail && this.args.model.isCurrentAccount) {
			options.push({
				icon: 'Pencil',
				text: 'Details',
				action: 'viewObject',
			});
		}

		return options;
	}

	@action
	doOptionsAction(actionName: string) {
		this[actionName as Action]();
	}

	@action
	viewObject() {
		const object = this.args.model.object;

		if (object instanceof Board) {
			this.mmBoardsService.openPreview(object);
		} else if (object instanceof BoardLoop) {
			this.mmBoardLoopsService.openPreviewPopup(object);
		} else if (object instanceof Report) {
			this.mmReportsService.openPreviewPopup(object);
		} else if (this.args.model.isEmail) {
			this.args.openEmailDetailsDialog?.(this.args.model.uniqueReferenceId);
		}
	}

	@action
	doNothing() {
		// A dormant method, quietly embodying the power of intentional inaction.
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		{{#if this.isVisibleOnScreen}}

			<tr class="table-row {{if this.showAll 'all-shown'}}" data-id={{@model.id}}>

				<td class="time">
					{{@model.relativeTime}}
					<ToolTipComponent @text={{@model.fullTime}} @position="top" />
				</td>

				{{#if @showAccount}}
					<td data-label="Account: ">
						<div class="account {{if @model.isAccountDeleted 'deleted'}}">
							<AvatarOrInitialsComponent
								@name={{@model.accountName}}
								@color={{@model.accountColor}}
							/>
							<span class="account-name">
								{{@model.accountName}}
								<ToolTipComponent
									@text={{@model.accountName}}
									@showOnlyIfTruncated={{true}}
								/>
							</span>
						</div>
					</td>
				{{/if}}

				<td class="message">
					{{{@model.sanitizedMessage}}}
				</td>

				<td data-label="User: ">
					<div class="user">
						{{#if @model.isSystem}}
							System
						{{else}}
							<AvatarOrInitialsComponent
								@name={{@model.user.name}}
								@avatar={{@model.user.avatar}}
							/>
							{{@model.user.name}}
						{{/if}}
					</div>
				</td>

				{{#if @showAction}}
					<td>
						<OptionsButtonComponent
							@options={{this.options}}
							@onAction={{this.doOptionsAction}}
						/>
					</td>
				{{/if}}

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>

			</tr>
		{{else}}
			<tr class="table-row" {{visibleCheckItem this.visibleChanged}}>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
